import { useRef, useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  formatCommercialJobNumber,
  type JobLayoutCard,
  type CommercialJobUpdateInput,
} from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { attachmentUrl } from '../../lib/image';
import { PageRoot, PageHeader } from '../ui/Page';
import { BackCrumb, SectionCard } from '../ui/Detail';
import { StatusBadge, Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { CustomerPicker } from '../ui/CustomerPicker';
import { Field, field } from '../create/Field';
import { useSession } from '../session';
import { useOnline } from '../ui/useConnection';
import {
  GoneState,
  StaleNotice,
  UnresolvedState,
  isNotFound,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { JobValueBuckets, stageTone } from './JobPipelineBoard';
import { IconJobs } from '../icons';
import { display, JOB_STATUS } from '../status';
import { JobQuotesPanel } from './JobQuotesPanel';
import { JobActivityFeed } from './JobActivityFeed';
import { JobCardLayoutEditor } from '../settings/JobLayoutSettingsCard';
import { CustomFieldsCard } from '../customfields/CustomFieldsCard';
import { UnderlineTabs, UnderlineTabStrip, UnderlineTabPanel } from '../ui/Tabs';
import { COMMERCIAL_JOB_SECTIONS, useUrlParam } from '../ui/pageTabs';

/** Persistent commercial identity; linked execution rows retain their Work Order IDs. */
export function JobWorkspace({ commercialJobId }: { commercialJobId: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const role = useSession().role;
  const online = useOnline();
  const mayEdit = can(role, 'commercialJob', 'update');
  const [customizing, setCustomizing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [section, setSection] = useUrlParam(
    'section',
    (raw) => COMMERCIAL_JOB_SECTIONS.find((value) => value === raw),
    'activity',
  );
  const q = useQuery(trpc.commercialJob.get.queryOptions({ commercialJobId }));
  const prefs = useQuery(trpc.userPrefs.jobView.queryOptions());
  const savePrefs = useMutation(
    trpc.userPrefs.setJobView.mutationOptions({
      onSuccess: (result) => qc.setQueryData(trpc.userPrefs.jobView.queryKey(), result),
    }),
  );
  const state = shownState(q, q.data);
  if (q.isError && isNotFound(q.error))
    return (
      <PageRoot>
        <BackCrumb to="/jobs" label="Jobs" />
        <GoneState icon={IconJobs} what="job" backTo="/jobs" backLabel="Back to jobs" />
      </PageRoot>
    );
  if (state !== 'ready')
    return (
      <PageRoot>
        <PageHeader title="Job" />
        <UnresolvedState
          state={state}
          icon={IconJobs}
          what="this job"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </PageRoot>
    );
  if (!q.data) return null;
  const { job, request, workOrders, withheld } = q.data;
  const cards: Record<JobLayoutCard, ReactNode> = {
    details: (
      <SectionCard title="Job details">
        <p className="mb-4 whitespace-pre-wrap text-sm">
          {job.description || 'No details recorded.'}
        </p>
        <dl className="space-y-3 text-sm">
          {[
            ['Owner', job.ownerName],
            ['Service type', job.serviceTypeLabel],
            ['Requested date', job.requestedOn],
            ['Requested window', job.requestedWindow],
            ['Expected close', job.expectedCloseOn],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-muted-foreground">{label}</dt>
              <dd className="mt-1 break-words">{value || '—'}</dd>
            </div>
          ))}
        </dl>
      </SectionCard>
    ),
    customer: (
      <SectionCard title="Customer">
        {job.customerId ? (
          <Link
            to="/customers/$customerId"
            params={{ customerId: job.customerId }}
            className="text-sm font-semibold hover:underline"
          >
            {job.customerName || 'Open customer'}
          </Link>
        ) : (
          <p className="text-sm text-muted-foreground">No customer assigned.</p>
        )}
      </SectionCard>
    ),
    property: (
      <SectionCard title="Service property">
        <p className="text-sm">{job.propertyLabel || 'No service address selected.'}</p>
      </SectionCard>
    ),
    quotes: withheld.includes('quotes') ? null : (
      <JobQuotesPanel commercialJobId={commercialJobId} />
    ),
    work_orders: (
      <SectionCard title="Work orders">
        {workOrders.length ? (
          <ul className="divide-y divide-border">
            {workOrders.map((workOrder) => (
              <li key={workOrder.id} className="space-y-2 py-3">
                <Link
                  to="/work-orders/$workOrderId"
                  params={{ workOrderId: workOrder.id }}
                  className="block text-sm font-semibold hover:underline"
                >
                  Work order #{workOrder.jobNumber}
                  <span className="mt-1 block font-normal">{workOrder.title}</span>
                </Link>
                <StatusBadge tone={display(JOB_STATUS, workOrder.status).tone}>
                  {display(JOB_STATUS, workOrder.status).label}
                </StatusBadge>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">No confirmed work orders.</p>
        )}
      </SectionCard>
    ),
    attachments: mayEdit ? (
      <JobAttachmentsCard commercialJobId={commercialJobId} />
    ) : null,
    custom_fields: mayEdit ? (
      <CustomFieldsCard
        entityType="commercial_job"
        entityId={commercialJobId}
        title="Custom fields"
      />
    ) : null,
  };
  const visible = (prefs.data?.cardOrder ?? []).filter(
    (key) => !prefs.data?.hiddenCards.includes(key),
  ) as JobLayoutCard[];
  const leftCards = new Set<JobLayoutCard>(['details', 'customer', 'property']);
  return (
    <PageRoot>
      <BackCrumb to="/jobs" label="Jobs" />
      <PageHeader
        title={job.title}
        sub={formatCommercialJobNumber(job.jobNumber)}
        right={
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge tone={stageTone(job.stageColor)}>{job.stageLabel}</StatusBadge>
            {mayEdit ? (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
                Edit Job
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCustomizing((value) => !value)}
            >
              Customize view
            </Button>
          </div>
        }
      />
      <StaleNotice
        show={refreshFailed(q, q.data) || q.isPaused}
        onRetry={() => void q.refetch()}
      />
      {customizing && prefs.data ? (
        <SectionCard title="Your Job view">
          <p className="mb-3 text-xs text-muted-foreground">
            These preferences apply to your account. Shop settings can keep a card hidden.
            Card order is respected within each side column.
          </p>
          {savePrefs.error ? (
            <Notice tone="danger" alert>
              {savePrefs.error.message}
            </Notice>
          ) : null}
          <JobCardLayoutEditor
            cardOrder={prefs.data.cardOrder as JobLayoutCard[]}
            hiddenCards={prefs.data.hiddenCards as JobLayoutCard[]}
            locked={!online || savePrefs.isPending || prefs.isFetching || prefs.isError}
            onChange={(next) => savePrefs.mutate(next)}
          />
        </SectionCard>
      ) : null}
      <UnderlineTabs value={section} onValueChange={setSection}>
        <div className="xl:hidden">
          <UnderlineTabStrip
            tabs={[
              { key: 'activity', label: 'Activity' },
              { key: 'details', label: 'Details' },
              { key: 'related', label: 'Related' },
            ]}
          />
        </div>
        <div className="grid items-start gap-4 xl:grid-cols-[minmax(220px,1fr)_minmax(400px,2fr)_minmax(260px,1fr)]">
          <UnderlineTabPanel
            value="details"
            keepMounted
            className="space-y-4 xl:data-[state=inactive]:block"
          >
            {!withheld.includes('financials') ? (
              <SectionCard title="Commercial value">
                <JobValueBuckets values={job} />
                <p className="mt-3 text-xs text-muted-foreground">
                  Separate amounts for scope at each commercial step. Invoice and payment
                  states stay on each work order.
                </p>
              </SectionCard>
            ) : null}
            {visible
              .filter((key) => leftCards.has(key))
              .map((key) => (
                <div key={key}>{cards[key]}</div>
              ))}
            {request ? (
              <SectionCard title="Original request">
                <p className="font-mono text-xs">Request #{request.requestNumber}</p>
                <p className="mt-2 text-sm">{request.contactName}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {request.source === 'office_job'
                    ? 'Recorded when the office created this Job'
                    : request.source.replaceAll('_', ' ')}
                </p>
              </SectionCard>
            ) : null}
          </UnderlineTabPanel>
          <UnderlineTabPanel
            value="activity"
            keepMounted
            className="space-y-4 xl:data-[state=inactive]:block"
          >
            {mayEdit ? (
              <JobActivityFeed commercialJobId={commercialJobId} />
            ) : (
              <Notice tone="neutral">
                This summary shows the work orders assigned to you.
              </Notice>
            )}
          </UnderlineTabPanel>
          <UnderlineTabPanel
            value="related"
            keepMounted
            className="space-y-4 xl:data-[state=inactive]:block"
          >
            {shownState(prefs, prefs.data) !== 'ready' ? (
              <Notice tone="warn">
                Your workspace layout could not load yet.
                <Button variant="ghost" size="sm" onClick={() => void prefs.refetch()}>
                  Reload layout
                </Button>
              </Notice>
            ) : null}
            <StaleNotice
              show={refreshFailed(prefs, prefs.data) || prefs.isPaused}
              onRetry={() => void prefs.refetch()}
            />
            {visible
              .filter((key) => !leftCards.has(key))
              .map((key) => (
                <div key={key}>{cards[key]}</div>
              ))}
            <Notice tone="info">
              Customer acceptance, office confirmation, and payment are separate steps.
              Moving a Job stage does not reserve stock or crews.
            </Notice>
          </UnderlineTabPanel>
        </div>
      </UnderlineTabs>
      {editing ? <JobFactsEditor job={job} onClose={() => setEditing(false)} /> : null}
    </PageRoot>
  );
}

function JobAttachmentsCard({ commercialJobId }: { commercialJobId: string }) {
  const trpc = useTRPC();
  const q = useQuery(
    trpc.attachment.forEntity.queryOptions({
      entityType: 'commercial_job',
      entityId: commercialJobId,
    }),
  );
  const state = shownState(q, q.data);
  return (
    <SectionCard title="Attachments">
      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          icon={IconJobs}
          what="this Job's attachments"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      ) : q.data?.length ? (
        <ul className="space-y-2">
          {q.data.map((file) => (
            <li key={file.id}>
              <a
                href={attachmentUrl(file.id)}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold hover:underline"
              >
                {file.filename || 'Open attachment'}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No attachments on this Job.</p>
      )}
    </SectionCard>
  );
}

function JobFactsEditor({
  job,
  onClose,
}: {
  job: {
    id: string;
    title: string;
    description: string | null;
    customerId: string | null;
    propertyId: string | null;
    ownerUserId: string | null;
    requestedOn: string | null;
    requestedWindow: string | null;
    expectedCloseOn: string | null;
    version: number;
  };
  onClose: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const online = useOnline();
  const [draft, setDraft] = useState({
    title: job.title,
    description: job.description ?? '',
    ownerUserId: job.ownerUserId ?? '',
    requestedOn: job.requestedOn ?? '',
    requestedWindow: job.requestedWindow ?? '',
    expectedCloseOn: job.expectedCloseOn ?? '',
  });
  const [who, setWho] = useState({
    customerId: job.customerId ?? '',
    propertyId: job.propertyId ?? '',
  });
  const version = useRef(job.version);
  const attempt = useRef<CommercialJobUpdateInput | null>(null);
  const members = useQuery(trpc.org.members.queryOptions());
  const update = useMutation(
    trpc.commercialJob.update.mutationOptions({
      onSuccess: () => {
        void qc.invalidateQueries();
        onClose();
      },
      onError: (error) => {
        if (error.data?.code === 'BAD_REQUEST' || error.data?.code === 'CONFLICT')
          attempt.current = null;
      },
    }),
  );
  const locked = update.isPending || attempt.current !== null;
  const save = () => {
    if (!online || update.isPending || !draft.title.trim()) return;
    attempt.current ??= {
      commercialJobId: job.id,
      expectedVersion: version.current,
      title: draft.title,
      description: draft.description || null,
      customerId: who.customerId || null,
      propertyId: who.propertyId || null,
      ownerUserId: draft.ownerUserId || null,
      requestedOn: draft.requestedOn || null,
      requestedWindow: draft.requestedWindow || null,
      expectedCloseOn: draft.expectedCloseOn || null,
      ...idem(),
    };
    update.mutate(attempt.current);
  };
  return (
    <Sheet
      open
      onClose={() => {
        if (!locked) onClose();
      }}
      title="Edit Job"
      footer={
        <Button
          className="w-full"
          disabled={!online || update.isPending || !draft.title.trim()}
          onClick={save}
        >
          {update.isPending
            ? 'Saving…'
            : attempt.current
              ? 'Retry this save'
              : 'Save Job'}
        </Button>
      }
    >
      <div className="space-y-4">
        {update.error ? (
          <Notice tone="danger" alert>
            {update.error.message}
          </Notice>
        ) : null}
        {!online ? (
          <Notice tone="warn">Reconnect to save. Your edits remain here.</Notice>
        ) : null}
        <fieldset disabled={locked} className="space-y-3">
          <Field label="Title">
            <input
              className={field}
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
            />
          </Field>
          <CustomerPicker
            customerId={who.customerId}
            propertyId={who.propertyId}
            onChange={setWho}
            requireProperty={false}
          />
          <Field label="Owner">
            <select
              className={field}
              value={draft.ownerUserId}
              onChange={(event) =>
                setDraft({ ...draft, ownerUserId: event.target.value })
              }
            >
              <option value="">Unassigned</option>
              {(members.data ?? [])
                .filter((member) =>
                  can(
                    member.role as ReturnType<typeof useSession>['role'],
                    'commercialJob',
                    'update',
                  ),
                )
                .map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Details">
            <textarea
              className={field + ' min-h-24 py-2'}
              value={draft.description}
              onChange={(event) =>
                setDraft({ ...draft, description: event.target.value })
              }
            />
          </Field>
          <Field label="Requested date">
            <input
              className={field}
              type="date"
              value={draft.requestedOn}
              onChange={(event) =>
                setDraft({ ...draft, requestedOn: event.target.value })
              }
            />
          </Field>
          <Field label="Requested window">
            <input
              className={field}
              value={draft.requestedWindow}
              onChange={(event) =>
                setDraft({ ...draft, requestedWindow: event.target.value })
              }
            />
          </Field>
          <Field label="Expected close">
            <input
              className={field}
              type="date"
              value={draft.expectedCloseOn}
              onChange={(event) =>
                setDraft({ ...draft, expectedCloseOn: event.target.value })
              }
            />
          </Field>
        </fieldset>
      </div>
    </Sheet>
  );
}
